// Queries use the *committed* date range so the page-level Calculate button controls when
// expensive recomputation runs. The period selector + Calculate live at page level
// (in the content top bar); this tab only consumes the committed values.
import { Alert, Box, CircularProgress, Stack } from "@mui/material";
import { DateTime } from "luxon";
import type { FC, ReactElement, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { BusyOverlay } from "../../../components/UIToolkit/molecules/BusyOverlay";
import type { PbjModelUpsertBody } from "../../../utils/models";
import { usePbjPrediction } from "../hooks/usePbjPrediction";
import { ScoringAndRecommendations } from "./ScoringAndRecommendations";

type Props = {
	committedStart: string;
	committedEnd: string;
	// Non-null: PbjModelPage's usePbjModelDraft always returns a seeded configDraft (never null).
	configDraft: PbjModelUpsertBody;
	// The persisted row's CMI pair — the hero compares the draft against it to decide whether the
	// admin has edited CMI since load (drives the CASPER effective-value display).
	savedCmi: { pccPdpmAvg: number; natNursingCaseMixIndex: number };
	onConfigDraftChange: (next: PbjModelUpsertBody) => void;
	// Persist + recompute a specific draft. Lets the manual-turnover dialog Save both
	// apply its edit and trigger a recalculation in one action.
	onCalculate: (next: PbjModelUpsertBody) => void;
	// True while the upsert mutation is running. Combined with the prediction's own
	// `isFetching` to keep the UI mounted but visually disabled during recalculation.
	isCalculating?: boolean;
	// Wraps the hero's "Change period" affordance in the page-level period/facility popover.
	renderChangePeriod: (trigger: ReactElement) => ReactNode;
	// Fired once a plan has been committed, so the page can surface the tracker it just populated.
	onPlanTracked: () => void;
};

// The scoring bands are calibrated for a CMS quarter (~90d). Within ±14d we still
// render the score without a warning; outside that window we surface a non-blocking
// banner so the user knows the result may not match what they'd submit to CMS.
const QUARTER_DAYS = 90;
const QUARTER_TOLERANCE_DAYS = 14;

export const PredictionTab: FC<Props> = ({
	committedStart,
	committedEnd,
	configDraft,
	savedCmi,
	onConfigDraftChange,
	onCalculate,
	isCalculating = false,
	renderChangePeriod,
	onPlanTracked,
}) => {
	const { t } = useTranslation();
	const prediction = usePbjPrediction(committedStart, committedEnd);
	const startDt = committedStart ? DateTime.fromISO(committedStart) : null;
	const endDt = committedEnd ? DateTime.fromISO(committedEnd) : null;

	// True initial load (no cached prediction at all). We *only* render the centered
	// spinner here. Subsequent refetches keep the previous data visible — see the
	// `placeholderData: keepPreviousData` on the query — and surface progress via the
	// disabled overlay below.
	if (prediction.isLoading && !prediction.data) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
				<CircularProgress data-testid="pbj-model-prediction-loading" />
			</Box>
		);
	}

	if (prediction.error || !prediction.data) {
		return (
			<Alert severity="error" data-testid="pbj-model-prediction-error">
				{t("pbj_model.prediction.failed")}
			</Alert>
		);
	}

	const isBusy = isCalculating || prediction.isFetching;

	// Tell the user when the prediction reflects only a partial window (BE clamps
	// the data window to "today" for ranges extending into the future).
	const dataThrough = prediction.data.dataThroughDate ? DateTime.fromISO(prediction.data.dataThroughDate) : null;
	const windowInFuture = dataThrough === null;
	const inProgress = dataThrough !== null && endDt !== null && dataThrough < endDt.startOf("day");

	// Scoring thresholds are calibrated for a CMS quarter (~90 days). Outside that
	// tolerance, render a non-blocking warning so users know the score may not match
	// what they'd submit to CMS.
	const rangeDays = startDt && endDt ? Math.round(endDt.diff(startDt, "days").days) + 1 : QUARTER_DAYS;
	const nonQuarterRange = Math.abs(rangeDays - QUARTER_DAYS) > QUARTER_TOLERANCE_DAYS;

	return (
		<Box sx={{ position: "relative" }} data-testid="pbj-model-prediction-tab">
			{(nonQuarterRange || windowInFuture || inProgress) && (
				<Stack spacing={2} sx={{ mb: 3 }}>
					{nonQuarterRange && (
						<Alert severity="warning" data-testid="pbj-model-prediction-non-quarter-banner">
							{t("pbj_model.warning.non_quarter_range")}
						</Alert>
					)}
					{windowInFuture && (
						<Alert severity="info" data-testid="pbj-model-prediction-future-banner">
							{t("pbj_model.prediction.future_quarter_banner")}
						</Alert>
					)}
					{inProgress && (
						<Alert severity="info" data-testid="pbj-model-prediction-in-progress-banner">
							{t("pbj_model.prediction.in_progress_caption", { date: dataThrough.toLocaleString(DateTime.DATE_MED) })}
						</Alert>
					)}
				</Stack>
			)}
			{/* Dim + freeze the prediction surface while a recalc is in flight, so the user keeps
			    spatial context (current scores, draft inputs) instead of staring at a spinner. */}
			<BusyOverlay busy={isBusy} dataTestId="pbj-model-prediction-recalculating">
				<ScoringAndRecommendations
					onPlanTracked={onPlanTracked}
					prediction={prediction.data}
					draft={configDraft}
					savedCmi={savedCmi}
					onDraftChange={onConfigDraftChange}
					onCalculate={onCalculate}
					startDate={committedStart}
					endDate={committedEnd}
					renderChangePeriod={renderChangePeriod}
				/>
			</BusyOverlay>
		</Box>
	);
};
